import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Button, Card, Col, Row, Table } from 'react-bootstrap';

import Container from 'components/shared/Container';
import RebateAnalysisUploadModal from 'components/rebate_analysis/RebateAnalysisUploadModal';
import RebateAnalysisUploadRow from 'components/rebate_analysis/RebateAnalysisUploadRow';
import DeviatedPriceAnalysisSection from 'components/rebate_analysis/DeviatedPriceAnalysisSection';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import SwalDialog, { areYouSure } from 'lib/utils/toast';
import { httpPost } from 'lib/networking/http';
import {
  REBATE_ANALYSIS_RUN_ANALYSIS,
  REBATE_ANALYSIS_UPLOAD,
  REBATE_ANALYSIS_UPLOADS,
  getURL,
} from 'lib/networking/endpoints';
import useDelete from 'lib/hooks/useDelete';
import useGet from 'lib/hooks/useGet';

function RebateAnalysisView() {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [runningUploadId, setRunningUploadId] = useState(null);

  const {
    data: uploadsData,
    loading,
    error,
    refetch,
    silentRefetch,
  } = useGet(REBATE_ANALYSIS_UPLOADS);

  const uploads = useMemo(() => uploadsData?.data || [], [uploadsData]);

  // pollTick advances on every attempt (success or failure) so a single failed
  // request never permanently stalls the loop. lastUploadsSigRef detects a
  // changed payload (by stable status signature) and resets the backoff to 3 s.
  const [pollTick, setPollTick] = useState(0);
  const pollDelayRef = useRef(3000);
  const lastUploadsSigRef = useRef('');

  useEffect(() => {
    const hasInProgress = uploads.some(u => {
      const s = u.status || '';
      return s === 'loading' || s.startsWith('running');
    });
    if (!hasInProgress) {
      pollDelayRef.current = 3000;
      return;
    }

    // Reset backoff when the payload changes. Compare a stable string signature
    // instead of object identity — silentRefetch always creates a new object even
    // when the data is byte-identical, which would otherwise keep the delay at 3 s.
    const uploadsSig = uploads.map(u => `${u.id}:${u.status}`).join(',');
    if (lastUploadsSigRef.current !== uploadsSig) {
      pollDelayRef.current = 3000;
      lastUploadsSigRef.current = uploadsSig;
    }

    // Pause when the tab is hidden to avoid unnecessary server load.
    if (document.visibilityState === 'hidden') {
      const onVisible = () => setPollTick(t => t + 1);
      document.addEventListener('visibilitychange', onVisible, { once: true });
      return () => document.removeEventListener('visibilitychange', onVisible);
    }

    const delay = pollDelayRef.current;
    const timer = setTimeout(() => {
      // Double the delay for the next attempt (cap 30 s); resets on success above.
      pollDelayRef.current = Math.min(delay * 2, 30000);
      silentRefetch();
      setPollTick(t => t + 1);
    }, delay);
    return () => clearTimeout(timer);
  }, [uploads, pollTick, silentRefetch]);

  const { deleteData: deleteUpload } = useDelete(
    REBATE_ANALYSIS_UPLOAD,
    false,
    () => silentRefetch(),
    () => SwalDialog('error', 'An error occurred while deleting the file.', 'Error'),
  );

  // eslint-disable-next-line space-before-function-paren
  const handleRunAnalysis = async upload => {
    setRunningUploadId(upload.id);
    try {
      const url = getURL(REBATE_ANALYSIS_RUN_ANALYSIS, { upload_id: upload.id });
      await httpPost(url);
      SwalDialog(
        'success',
        'Analysis started. Download the Report XLSX once status shows "completed".',
      );
      silentRefetch();
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      SwalDialog('error', `Failed to start analysis: ${msg}`, 'Error');
      silentRefetch();
    } finally {
      setRunningUploadId(null);
    }
  };

  return (
    <Container>
      <div className="mb-4">
        <h3 className="font-weight-bold ml-3">Rebate Analysis</h3>
      </div>
      <Row className="mb-4">
        <Col md={5}>
          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title className="mb-3 font-weight-bold">Upload Rebate File</Card.Title>
              <Card.Text className="text-muted">
                Upload a CIL Excel file to run the rebate analysis process for a Distribution
                Partner.
              </Card.Text>
              <Button variant="primary" onClick={() => setShowUploadModal(true)}>
                Upload File
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <h5 className="font-weight-bold ml-3 mb-3">Uploaded Files</h5>
      {loading ? (
        <LoadingSpinner short />
      ) : error && !uploadsData ? (
        <p className="text-danger ml-3">Failed to load uploads. Please refresh the page.</p>
      ) : (
        <Table bordered striped hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>File Name</th>
              <th>Action</th>
              <th>Downloads</th>
              <th>Status</th>
              <th>Uploaded At</th>
              <th>Uploaded By</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {uploads.length ? (
              uploads.map(upload => (
                <RebateAnalysisUploadRow
                  key={upload.id}
                  upload={upload}
                  onDelete={() =>
                    areYouSure(
                      () => deleteUpload({ upload_id: upload.id }),
                      'This will permanently delete the upload and its Snowflake rows.',
                    )
                  }
                  onRunAnalysis={() => handleRunAnalysis(upload)}
                  isRunning={runningUploadId === upload.id}
                />
              ))
            ) : (
              <tr>
                <td className="text-center" colSpan="100%">
                  No files uploaded yet.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
      <DeviatedPriceAnalysisSection />
      {showUploadModal && (
        <RebateAnalysisUploadModal
          onHide={() => setShowUploadModal(false)}
          onSuccess={() => {
            SwalDialog('success', 'File uploaded successfully.');
            refetch();
          }}
        />
      )}
    </Container>
  );
}

export default RebateAnalysisView;
